import React from 'react';

import { UnderEditContext } from '@neptune/shared/common-domain';
import { logger } from '@neptune/shared/core-logger-util';

export function useUnderEditContext() {
  const {
    underEdit,
    underEdit$,
    startEdit: rawStartEdit,
    stopEdit: rawStopEdit,
    startEditWindow: rawStartEditWindow,
    stopEditWindow: rawStopEditWindow,
  } = React.useContext(UnderEditContext);

  const editStartCountRef = React.useRef(0);
  const isEditWindowStartedRef = React.useRef(false);

  const startEdit = React.useCallback(() => {
    editStartCountRef.current += 1;
    rawStartEdit();
  }, [rawStartEdit]);

  const stopEdit = React.useCallback(() => {
    if (editStartCountRef.current > 0) {
      editStartCountRef.current -= 1;
    }

    rawStopEdit();
  }, [rawStopEdit]);

  const startEditWindow = React.useCallback(
    (windowCloseDelay: number) => {
      isEditWindowStartedRef.current = true;
      rawStartEditWindow(windowCloseDelay);
    },
    [rawStartEditWindow],
  );

  const stopEditWindow = React.useCallback(() => {
    isEditWindowStartedRef.current = false;
    rawStopEditWindow();
  }, [rawStopEditWindow]);

  React.useEffect(() => {
    return () => {
      if (editStartCountRef.current > 0) {
        logger.logMessage('use-under-edit-context: component cleanup while edit is active', {
          module: 'use-under-edit-context',
          pendingEditStarts: editStartCountRef.current,
        });

        while (editStartCountRef.current > 0) {
          rawStopEdit();
          editStartCountRef.current -= 1;
        }
      }

      if (isEditWindowStartedRef.current) {
        logger.logMessage('use-under-edit-context: component cleanup while edit window is active', {
          module: 'use-under-edit-context',
          isEditWindowStarted: true,
        });

        rawStopEditWindow();
        isEditWindowStartedRef.current = false;
      }
    };
  }, [rawStopEdit, rawStopEditWindow]);

  return React.useMemo(
    () => ({
      underEdit,
      underEdit$,
      startEdit,
      stopEdit,
      startEditWindow,
      stopEditWindow,
    }),
    [underEdit, underEdit$, startEdit, stopEdit, startEditWindow, stopEditWindow],
  );
}
